/**
 * 상단 바 (Stripe Dashboard형 — 로고, 전역 검색, Conscious 배지, 사용자 메뉴)
 * UI_STANDARD_APPLICATION_PLAN.md §3.1, §5 Phase 1.2, Phase 3
 * H3/X2: GlobalSearchContext + GlobalSearchInput · ⌘K/Ctrl+K → GlobalSearchModal
 */

import React, { useMemo } from 'react';
import { Button, Space, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { BusinessShellBrand } from './BusinessShellBrand';
import { GlobalSearchInput } from './GlobalSearchInput';
import { BusinessInboxStrip } from './BusinessInboxStrip';
import { ConsciousAlertsBadge } from './ConsciousAlertsBadge';
import { NotificationPanel } from './NotificationPanel';
import { useTenant } from '../../contexts/TenantContext';
import { useMenuVisibility } from '../../hooks/ui/useMenuVisibility';
import { getCurrentTokenPayload } from '../../utils/jwtUtils';
import {
  resolveActorDisplayName,
  resolveUserFacingRoleLabel,
} from '../../utils/actorOrientationLabels';

export interface AppTopBarProps {
  /** 모바일에서 메뉴(햄버거) 버튼 표시 및 클릭 시 호출 */
  showMenuButton?: boolean;
  onMenuClick?: () => void;
}

const SETTINGS_HUB_PATH = '/system/settings';

const { Text } = Typography;

export const AppTopBar: React.FC<AppTopBarProps> = ({ showMenuButton, onMenuClick }) => {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { canAccessPath } = useMenuVisibility();
  const tenantId = currentTenant?.id ?? 'default_tenant';

  const actorPayload = getCurrentTokenPayload();
  const actorDisplayName = resolveActorDisplayName(actorPayload);
  const actorRoleLabel = resolveUserFacingRoleLabel(actorPayload);
  const actorTenantName = currentTenant?.displayName || currentTenant?.name || '';

  const userMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '프로필',
        onClick: () => navigate('/entity'),
      },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: '설정',
        onClick: () => navigate(SETTINGS_HUB_PATH),
      },
    ];

    if (canAccessPath('/system/cross-layer-view')) {
      items.push({
        key: 'cross-layer-view',
        icon: <ApartmentOutlined />,
        label: '4계층 탐색',
        onClick: () => navigate('/system/cross-layer-view'),
      });
    }

    items.push(
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '로그아웃',
        onClick: () => navigate('/login'),
      }
    );

    return items;
  }, [canAccessPath, navigate]);

  const topBarClassName = showMenuButton
    ? 'business-topbar business-topbar--mobile'
    : 'business-topbar';

  return (
    <div className={topBarClassName} role="banner" aria-label="앱 상단 바">
      <Space size="middle">
        {showMenuButton && onMenuClick && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            aria-label="메뉴 열기"
            data-testid="app-layout-menu-button"
          />
        )}
        <BusinessShellBrand interactive className="business-topbar__logo im-text-gradient" />
      </Space>

      <div
        className="business-topbar__search"
        data-testid="app-topbar-global-search"
        style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
      >
        <Text
          type="secondary"
          className="business-topbar__search-scope"
          data-testid="app-topbar-search-scope-label"
          style={{ fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          전역
        </Text>
        <GlobalSearchInput tenantId={tenantId} className="business-topbar__search" />
      </div>

      <Space size={showMenuButton ? 4 : 'middle'}>
        <BusinessInboxStrip />
        <ConsciousAlertsBadge />
        <NotificationPanel />
        <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<UserOutlined />}
            aria-label="계정 메뉴"
            className="business-topbar__actor-trigger"
          >
            <span
              className="business-topbar__actor-orientation"
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                lineHeight: 1.2,
              }}
            >
              <span className="business-topbar__actor-name" data-testid="app-topbar-actor-name">
                {actorDisplayName}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 11,
                  opacity: 0.75,
                }}
              >
                <span className="business-topbar__tenant-name" data-testid="app-topbar-tenant-name">
                  {actorTenantName}
                </span>
                {actorTenantName && <span aria-hidden="true">·</span>}
                <span className="business-topbar__role-label" data-testid="app-topbar-role-label">
                  {actorRoleLabel}
                </span>
              </span>
            </span>
          </Button>
        </Dropdown>
      </Space>
    </div>
  );
};

export default AppTopBar;
